import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Checkbox, FormControlLabel, MenuItem, Select, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MathEditor from "../../components/RichText/MathEditor";
import NunitoText from "../../components/general/NunitoText";
import ReusableButton from "../../components/general/ReusableButton";
import { showInfoSwal } from "../../functions/alert";
import { getTranslatorCollectionTasks, getTranslatorGradeLanguages } from "../../functions/getData";
import {
  finalizeTranslation,
  markTranslationTaskDone,
  saveTranslationInstructions,
} from "../../functions/postData";
import {
  getDraft,
  isSkipped,
  isSubmitted,
  markSubmitted,
  setDraft,
  setSkipped,
} from "./sampleTranslateData";
import "./translate.css";
import {
  KIND_LABEL,
  doneCount,
  findPaperContext,
  findTask,
  instructionsPayload,
  mapCollectionTasks,
  partState,
  taskDonePayload,
  totalParts,
} from "./translationParts";

function EnglishBlock({ value, rich }) {
  if (rich) {
    return (
      <div
        className="translate-english rich"
        dangerouslySetInnerHTML={{ __html: value || "" }}
      />
    );
  }
  return <div className="translate-english">{value}</div>;
}

function FieldPair({
  label,
  english,
  gradeId,
  language,
  part,
  field,
  rich,
  disabled,
  value: valueProp,
  onValueChange,
}) {
  const external = valueProp !== undefined;
  const stored = external ? valueProp : getDraft(gradeId, language, part, field);
  const [text, setText] = useState(stored);

  useEffect(() => {
    setText(stored);
  }, [stored]);

  const change = (next) => {
    setText(next);
    if (onValueChange) onValueChange(next);
    else setDraft(gradeId, language, part, field, next);
  };

  return (
    <div className="translate-pair">
      <NunitoText
        value={label}
        fontSize={14}
        fontWeight={700}
        align="left"
        marginBottom={6}
      />
      <div className="translate-cols">
        <div>
          <span className="translate-field-label">English</span>
          <EnglishBlock value={english} rich={rich} />
        </div>
        <div className={disabled ? "translate-locked" : undefined}>
          <span className="translate-field-label">{language}</span>
          {rich ? (
            <MathEditor
              key={`${gradeId}|${language}|${part}|${field}|${disabled}`}
              value={text}
              readOnly={disabled}
              placeholder={`${language} translation`}
              onChange={disabled ? undefined : change}
            />
          ) : (
            <TextField
              fullWidth
              size="small"
              value={text}
              disabled={disabled}
              placeholder={`${language} translation`}
              onChange={(event) => change(event.target.value)}
              sx={{ background: disabled ? "#eceff3" : "#fff" }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Outline({ paper, gradeId, language, node, setNode, partStatus }) {
  const statusFor = (part) =>
    partStatus ? partStatus(part) : partState(gradeId, language, part);
  return (
    <div className="translate-outline">
      <button
        type="button"
        className={`translate-node ${node === "cover" ? "active" : ""}`}
        onClick={() => setNode("cover")}
      >
        <span>Cover</span>
        <small>{statusFor("cover")}</small>
      </button>
      {paper.sections.map((section) => {
        const sectionPart = `section:${section.id}`;
        return (
          <div key={section.id}>
            <button
              type="button"
              className={`translate-node ${node === sectionPart ? "active" : ""}`}
              onClick={() => setNode(sectionPart)}
            >
              <span>{section.name}</span>
              <small>{statusFor(sectionPart)}</small>
            </button>
            {section.tasks.map((task) => {
              const taskPart = `task:${task.id}`;
              const state = statusFor(taskPart);
              return (
                <button
                  key={task.id}
                  type="button"
                  className={`translate-node child ${node === taskPart ? "active" : ""}`}
                  onClick={() => setNode(taskPart)}
                >
                  <span>{task.label}</span>
                  <small>{state === "Open" ? KIND_LABEL[task.kind] || task.kind : state}</small>
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function SkipCheckbox({ checked, part, onChange, disabled }) {
  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={!!checked}
          disabled={disabled}
          onChange={(event) => onChange(part, event.target.checked)}
          sx={{ color: "#144A94", "&.Mui-checked": { color: "#144A94" } }}
        />
      }
      label="No translation needed"
    />
  );
}

export default function TranslateWorkspace() {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const competitionId = params.get("competitionId") || "";
  const assignmentId = params.get("assignmentId") || "";
  const gradeId = params.get("gradeId") || "";
  const language = params.get("language") || "";
  const languageId = params.get("languageId") || "";
  const competitionName = params.get("competition") || "";
  const gradeName = params.get("grade") || "";
  const collectionName = params.get("collection") || "";
  const languageOptions = params.getAll("languages");
  const context = findPaperContext(competitionId, gradeId);
  const [node, setNode] = useState("cover");
  const [gradeLanguages, setGradeLanguages] = useState([]);
  const [workspace, setWorkspace] = useState(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finalizedLanguages, setFinalizedLanguages] = useState({});
  const savingRef = useRef(false);
  const draftsRef = useRef({});
  const [, refresh] = useState(0);
  const bump = () => refresh((count) => count + 1);

  useEffect(() => {
    document.title = "Translate";
  }, []);

  useEffect(() => {
    if (!/^\d+$/.test(gradeId)) return undefined;
    const controller = new AbortController();
    let active = true;
    setGradeLanguages([]);
    getTranslatorGradeLanguages(gradeId, controller.signal)
      .then((rows) => {
        if (!active) return;
        const list = Array.isArray(rows) ? rows : [];
        setGradeLanguages(list);
        const current = new URLSearchParams(window.location.search).get("languageId");
        const selected = list.some((item) => String(item.id) === current);
        if (!selected && list[0]) {
          const next = new URLSearchParams(window.location.search);
          next.set("languageId", String(list[0].id));
          history.replace({ pathname: "/translate/paper", search: next.toString() });
        }
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        if (active) setGradeLanguages([]);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [gradeId, history]);

  useEffect(() => {
    if (!/^\d+$/.test(gradeId) || !/^\d+$/.test(languageId)) return undefined;
    const controller = new AbortController();
    let active = true;
    setWorkspace(null);
    setWorkspaceLoading(true);
    setNode("cover");
    getTranslatorCollectionTasks(gradeId, languageId, controller.signal)
      .then((res) => {
        if (!active) return;
        setWorkspace(mapCollectionTasks(res));
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        if (active) setWorkspace(null);
      })
      .finally(() => {
        if (active) setWorkspaceLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [gradeId, languageId]);

  useEffect(() => {
    draftsRef.current = workspace?.drafts || {};
  }, [workspace]);

  useEffect(() => {
    const found = findPaperContext(competitionId, gradeId);
    const options = found?.grade.languages || languageOptions;
    if (!options.length) return;
    if (language && options.includes(language)) return;
    const next = new URLSearchParams(location.search);
    next.set("language", options[0]);
    history.replace({ pathname: "/translate/paper", search: next.toString() });
  }, [competitionId, gradeId, history, language, location.search]);

  const changeLanguage = (nextLanguage) => {
    const next = new URLSearchParams(location.search);
    next.set("language", nextLanguage);
    history.replace({ pathname: "/translate/paper", search: next.toString() });
  };

  const changeGradeLanguage = (nextLanguageId) => {
    const next = new URLSearchParams(location.search);
    next.set("languageId", nextLanguageId);
    history.replace({ pathname: "/translate/paper", search: next.toString() });
  };

  const locked = !!(finalizedLanguages[languageId] || workspace?.finalized);

  const updateDraft = (part, field, value) => {
    if (locked) return;
    const key = `${part}|${field}`;
    draftsRef.current = { ...draftsRef.current, [key]: value };
    setWorkspace((current) => {
      if (!current) return current;
      return {
        ...current,
        drafts: { ...current.drafts, [key]: value },
      };
    });
  };

  const refreshWorkspace = async () => {
    const res = await getTranslatorCollectionTasks(gradeId, languageId);
    const mapped = mapCollectionTasks(res);
    if (mapped) setWorkspace(mapped);
  };

  const beginSave = () => {
    if (savingRef.current || locked) return false;
    savingRef.current = true;
    setSaving(true);
    return true;
  };

  const endSave = () => {
    savingRef.current = false;
    setSaving(false);
  };

  const saveInstructions = async () => {
    if (!workspace || !beginSave()) return;
    try {
      await saveTranslationInstructions(
        instructionsPayload({ ...workspace, drafts: draftsRef.current }, gradeId, languageId)
      );
      await refreshWorkspace();
    } catch (error) {
      if (error?.name === "AbortError") return;
    } finally {
      endSave();
    }
  };

  const saveTask = async (taskId, noTranslationNeeded) => {
    if (!workspace || !beginSave()) return;
    const task = findTask(workspace.paper, taskId);
    if (!task) {
      endSave();
      return;
    }
    try {
      await markTranslationTaskDone(
        gradeId,
        task.id,
        taskDonePayload(task, draftsRef.current, languageId, noTranslationNeeded)
      );
      await refreshWorkspace();
    } catch (error) {
      if (error?.name === "AbortError") return;
    } finally {
      endSave();
    }
  };

  const toggleWorkspaceSkip = (part, checked) => {
    const taskId = part.startsWith("task:") ? part.slice("task:".length) : "";
    if (!taskId) return;
    saveTask(taskId, checked);
  };

  const markWorkspaceDone = () => {
    if (node === "cover" || node.startsWith("section:")) {
      saveInstructions();
      return;
    }
    if (node.startsWith("task:")) saveTask(node.slice("task:".length), false);
  };

  const finalizeLanguage = async () => {
    if (!workspace || savingRef.current || locked) return;
    const result = await Swal.fire({
      title: "Finalize this language?",
      text: "After this, the translations for this language cannot be changed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Finalize",
      cancelButtonText: "Cancel",
      customClass: { container: "swalContainer" },
    });
    if (!result.isConfirmed || !beginSave()) return;
    try {
      await finalizeTranslation({
        translation_assignment_grade_id: Number(gradeId),
        language_id: Number(languageId),
        confirm_finalize: true,
      });
      setFinalizedLanguages((current) => ({ ...current, [languageId]: true }));
      await refreshWorkspace();
      showInfoSwal("Finalized", "This language can no longer be changed.");
    } catch (error) {
      if (error?.name === "AbortError") return;
    } finally {
      endSave();
    }
  };

  const workspaceStatus = (part) => {
    if (!workspace) return "Open";
    const taskId = part.startsWith("task:") ? part.slice("task:".length) : "";
    if (taskId && workspace.skipped[taskId]) return "Not needed";
    return workspace.done[part] ? "Submitted" : "Open";
  };

  const toggleSkip = (part, checked) => {
    setSkipped(gradeId, language, part, checked);
    bump();
  };

  const submit = () => {
    markSubmitted(gradeId, language, node);
    bump();
  };

  if (!context) {
    if (!assignmentId || !gradeId) {
      return (
        <Box className="wrapperBox" sx={{ p: 4 }}>
          <NunitoText
            value="Choose a grade from the translate list."
            fontSize={20}
            fontWeight={700}
            color="#144A94"
            align="left"
          />
          <ReusableButton
            text="Translate"
            fontSize={14}
            bgColor="#5E75C3"
            height={40}
            br={8}
            marginTop={16}
            to="/translate"
          />
        </Box>
      );
    }

    return (
      <Box className="wrapperBox">
        <div className="firstRowContainer dashboardAndSelfBtnDiv">
          <ReusableButton
            text="Dashboard"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            width={125}
            br={18}
            to="/dashboard"
            iconType="home"
          />
          <ChevronRightIcon />
          <ReusableButton
            text="Translate"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            br={18}
            to="/translate"
          />
          <ChevronRightIcon />
          <ReusableButton
            text={gradeName || "Grade"}
            fontSize={14}
            bgColor="#5E75C3"
            height={36}
            br={18}
          />
        </div>
        <div className="formContainer" style={{ marginTop: 12 }}>
          <NunitoText
            value={`${competitionName} · ${gradeName}`}
            fontSize={32}
            fontWeight={700}
            italic
            color="#144A94"
            align="left"
          />
          <NunitoText
            value={workspace?.collectionName || collectionName}
            fontSize={16}
            color="#707070"
            align="left"
            marginTop={8}
          />
          <div
            className="translate-head-tools"
            style={{ justifyContent: "space-between", marginTop: 16 }}
          >
            <Select
              size="small"
              value={
                gradeLanguages.some((item) => String(item.id) === languageId)
                  ? languageId
                  : ""
              }
              displayEmpty
              disabled={!gradeLanguages.length}
              onChange={(event) => changeGradeLanguage(event.target.value)}
              sx={{ minWidth: 280, background: "#fff" }}
            >
              {gradeLanguages.map((item) => (
                <MenuItem key={item.id} value={String(item.id)}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
            {workspace && (
              <>
                <NunitoText
                  value={`${workspace.completed}/${workspace.total} done`}
                  fontSize={16}
                  fontWeight={700}
                  align="left"
                />
                {locked ? (
                  <NunitoText
                    value="Finalized"
                    fontSize={14}
                    fontWeight={700}
                    color="#1f7a4d"
                    align="left"
                  />
                ) : (
                  <ReusableButton
                    text="Finalize language"
                    fontSize={14}
                    bgColor="#F16774"
                    height={40}
                    br={8}
                    allowSpam
                    disabledButton={saving}
                    onClick={finalizeLanguage}
                  />
                )}
              </>
            )}
          </div>
          {workspaceLoading && (
            <NunitoText
              value="Loading paper..."
              fontSize={16}
              color="#707070"
              align="left"
              marginTop={16}
            />
          )}
          {!workspaceLoading && languageId && !workspace && (
            <NunitoText
              value="This paper could not be loaded."
              fontSize={16}
              color="#707070"
              align="left"
              marginTop={16}
            />
          )}
          {workspace && (
            <div className="translate-split" style={{ marginTop: 16 }}>
              <Outline
                paper={workspace.paper}
                gradeId={gradeId}
                language={languageId}
                node={node}
                setNode={setNode}
                partStatus={workspaceStatus}
              />
              <WorkPane
                paper={workspace.paper}
                gradeId={gradeId}
                language={
                  gradeLanguages.find((item) => String(item.id) === languageId)?.name ||
                  "Translation"
                }
                node={node}
                setNode={setNode}
                skipped={
                  node.startsWith("task:") &&
                  !!workspace.skipped[node.slice("task:".length)]
                }
                submitted={!!workspace.done[node]}
                onSkip={toggleWorkspaceSkip}
                onSubmit={markWorkspaceDone}
                drafts={workspace.drafts}
                onDraft={updateDraft}
                locked={locked}
                busy={saving}
                isSkippedPart={(part) =>
                  !!workspace.skipped[part.slice("task:".length)]
                }
                isSubmittedPart={workspaceStatus}
              />
            </div>
          )}
        </div>
      </Box>
    );
  }

  if (!context.grade.languages.includes(language)) return null;

  const { competition, grade, paper } = context;
  const done = doneCount(gradeId, language);
  const total = totalParts(gradeId);
  const skipped = node.startsWith("task:") && isSkipped(gradeId, language, node);
  const submitted = isSubmitted(gradeId, language, node);

  return (
    <Box className="wrapperBox">
      <div className="firstRowContainer dashboardAndSelfBtnDiv">
        <ReusableButton
          text="Dashboard"
          fontSize={14}
          bgColor="#F16774"
          height={36}
          width={125}
          br={18}
          to="/dashboard"
          iconType="home"
        />
        <ChevronRightIcon />
        <ReusableButton
          text="Translate"
          fontSize={14}
          bgColor="#F16774"
          height={36}
          br={18}
          to="/translate"
        />
        <ChevronRightIcon />
        <ReusableButton
          text={grade.grade}
          fontSize={14}
          bgColor="#5E75C3"
          height={36}
          br={18}
        />
      </div>
      <div className="formContainer" style={{ marginTop: 12 }}>
        <div className="translate-head-tools" style={{ justifyContent: "space-between", marginBottom: 16 }}>
          <NunitoText
            value={`${competition.name} · ${grade.grade}`}
            fontSize={32}
            fontWeight={700}
            italic
            color="#144A94"
            align="left"
          />
          <div className="translate-head-tools">
            <NunitoText
              value={grade.collection}
              fontSize={16}
              color="#707070"
              align="left"
            />
            <Select
              size="small"
              value={language}
              onChange={(event) => changeLanguage(event.target.value)}
              sx={{ minWidth: 150, background: "#fff" }}
            >
              {grade.languages.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
            <NunitoText
              value={`${done}/${total} done`}
              fontSize={16}
              fontWeight={700}
              align="left"
            />
          </div>
        </div>
        <div className="translate-split">
          <Outline
            paper={paper}
            gradeId={gradeId}
            language={language}
            node={node}
            setNode={setNode}
          />
          <WorkPane
            paper={paper}
            gradeId={gradeId}
            language={language}
            node={node}
            setNode={setNode}
            skipped={skipped}
            submitted={submitted}
            onSkip={toggleSkip}
            onSubmit={submit}
          />
        </div>
      </div>
    </Box>
  );
}

function WorkPane({
  paper,
  gradeId,
  language,
  node,
  setNode,
  skipped,
  submitted,
  onSkip,
  onSubmit,
  drafts,
  onDraft,
  locked,
  busy,
  isSkippedPart,
  isSubmittedPart,
}) {
  const fieldValue = (part, field) => {
    if (!drafts) return {};
    return {
      value: drafts[`${part}|${field}`] || "",
      onValueChange: (next) => onDraft(part, field, next),
    };
  };
  const skippedPart = (part) =>
    isSkippedPart ? isSkippedPart(part) : isSkipped(gradeId, language, part);
  const statusFor = (part) =>
    isSubmittedPart ? isSubmittedPart(part) : partState(gradeId, language, part);
  let title = "Instructions / cover page";
  let body = (
    <FieldPair
      label="Cover"
      english={paper.cover}
      gradeId={gradeId}
      language={language}
      part="cover"
      field="body"
      rich
      disabled={locked || busy}
      {...fieldValue("cover", "body")}
    />
  );

  if (node.startsWith("section:")) {
    const section = paper.sections.find((item) => `section:${item.id}` === node);
    if (section) {
      title = section.name;
      body = (
        <>
          <FieldPair
            label="Section instructions"
            english={section.english}
            gradeId={gradeId}
            language={language}
            part={node}
            field="body"
            rich
            disabled={locked || busy}
            {...fieldValue(node, "body")}
          />
          <NunitoText
            value={`Tasks in ${section.name}`}
            fontSize={14}
            fontWeight={700}
            align="left"
            marginTop={8}
          />
          {section.tasks.map((task) => {
            const part = `task:${task.id}`;
            const state = statusFor(part);
            return (
              <div className="translate-task-row" key={task.id}>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <button
                    type="button"
                    className="translate-node"
                    style={{ width: "auto", padding: 0, border: 0 }}
                    onClick={() => setNode(part)}
                  >
                    <span style={{ color: "#144A94", fontWeight: 650 }}>{task.label}</span>
                  </button>
                  <span style={{ color: "#707070", fontSize: 12 }}>
                    {KIND_LABEL[task.kind] || task.kind}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <SkipCheckbox
                    checked={skippedPart(part)}
                    part={part}
                    onChange={onSkip}
                    disabled={locked || busy}
                  />
                  <span style={{ color: "#707070", fontSize: 13 }}>{state}</span>
                </div>
              </div>
            );
          })}
        </>
      );
    }
  }

  if (node.startsWith("task:")) {
    const taskId = node.slice("task:".length);
    let sectionName = "";
    let task = null;
    paper.sections.forEach((section) => {
      const found = section.tasks.find((item) => String(item.id) === String(taskId));
      if (found) {
        sectionName = section.name;
        task = found;
      }
    });
    if (task) {
      title = `${sectionName} · ${task.label}`;
      body = (
        <>
          <div className="translate-task-row" style={{ borderTop: 0, paddingTop: 0 }}>
            <span style={{ color: "#707070", fontSize: 12 }}>
              {KIND_LABEL[task.kind] || task.kind}
              {task.identifier ? ` · ${task.identifier}` : ""}
            </span>
            <SkipCheckbox
              checked={skippedPart(node)}
              part={node}
              onChange={onSkip}
              disabled={locked || busy}
            />
          </div>
          {task.kind !== "mcq" && !task.answers?.length && (
            <NunitoText
              value="Label and content submit together. This answer type has no choice text."
              fontSize={14}
              color="#707070"
              align="left"
              marginBottom={12}
            />
          )}
          <FieldPair
            label="Task label"
            english={task.englishLabel}
            gradeId={gradeId}
            language={language}
            part={node}
            field="label"
            disabled={skipped || locked || busy}
            {...fieldValue(node, "label")}
          />
          <FieldPair
            label="Task content"
            english={task.englishContent}
            gradeId={gradeId}
            language={language}
            part={node}
            field="content"
            rich
            disabled={skipped || locked || busy}
            {...fieldValue(node, "content")}
          />
          {task.answers?.length > 0 &&
            task.kind !== "open_ended" &&
            task.answers.map((answer, index) => (
              <FieldPair
                key={answer.id || index}
                label={
                  answer.correct
                    ? `Answer ${answer.position || index + 1} · correct`
                    : `Answer ${answer.position || index + 1}`
                }
                english={answer.english}
                gradeId={gradeId}
                language={language}
                part={node}
                field={`answer:${index}`}
                rich={answer.rich !== false && task.kind === "mcq"}
                disabled={skipped || locked || busy}
                {...fieldValue(node, `answer:${index}`)}
              />
            ))}
        </>
      );
    }
  }

  return (
    <section className="translate-work">
      <div className="translate-work-head">
        <NunitoText value={title} fontSize={18} fontWeight={700} align="left" />
        {locked ? (
          <NunitoText
            value="This language is finalized"
            fontSize={14}
            color="#707070"
            align="right"
          />
        ) : skipped ? (
          <NunitoText
            value="Marked as not needing a translation"
            fontSize={14}
            color="#707070"
            align="right"
          />
        ) : (
          <ReusableButton
            text={busy ? "Saving..." : submitted ? "Update translation" : "Submit translation"}
            fontSize={14}
            bgColor={submitted ? "#fff" : "#5E75C3"}
            borderColor={submitted ? "#5E75C3" : undefined}
            color={submitted ? "#5E75C3" : "#fff"}
            height={40}
            br={8}
            allowSpam
            disabledButton={busy}
            onClick={onSubmit}
          />
        )}
      </div>
      {body}
    </section>
  );
}
